import React, { useEffect, useState, useCallback } from "react";
import { Snackbar, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const AUTO_UPDATE_DELAY_MS = 30_000;
const REDISPLAY_AFTER_DISMISS_MS = 120_000;

function applyUpdate(registration: ServiceWorkerRegistration) {
  if (registration.waiting) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
  // Don't reload here — controllerchange listener handles it
  // after the new SW has actually taken control.
}

export default function UpdatePrompt() {
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Fix 1 & 4: Reload only after the new SW has taken control.
  // Also handles cross-tab: if another tab triggers skipWaiting,
  // all tabs get controllerchange and reload.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    // On first-ever SW install controller is null — no reload needed
    // (the page already has the latest assets from the network).
    if (!sw.controller) return;
    const onControllerChange = () => {
      window.location.reload();
    };
    sw.addEventListener("controllerchange", onControllerChange);
    return () => {
      sw.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const reg = (e as CustomEvent<ServiceWorkerRegistration>).detail;
      setRegistration(reg);
    };
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);

  // Auto-update after timeout if user hasn't dismissed
  useEffect(() => {
    if (!registration || dismissed) return;
    const timer = setTimeout(
      () => applyUpdate(registration),
      AUTO_UPDATE_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [registration, dismissed]);

  // Fix 3: Re-show the prompt after 2 minutes if user dismissed it
  useEffect(() => {
    if (!dismissed || !registration) return;
    const timer = setTimeout(
      () => setDismissed(false),
      REDISPLAY_AFTER_DISMISS_MS
    );
    return () => clearTimeout(timer);
  }, [dismissed, registration]);

  const handleUpdate = useCallback(() => {
    if (registration) applyUpdate(registration);
  }, [registration]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  return (
    <Snackbar
      open={!!registration && !dismissed}
      message="Nueva versión disponible"
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      action={
        <>
          <Button color="primary" size="small" onClick={handleUpdate}>
            Actualizar
          </Button>
          <IconButton size="small" color="inherit" onClick={handleDismiss} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    />
  );
}
